/**
 * @fileoverview Embedded building-owned blocks (`systems`, `intel`,
 *               `riskProfile`, `riskAssessment`) for the complex-inspections
 *               inspectable-entity contract.
 * @module import-core/contracts/complex-inspections/embeddedBuildingSchemas
 *
 * Responsibilities:
 * - Validate the polymorphic `inspectableEntities.systems[]` block: every
 *   building system (alarm, extinguishing, backup power, ...) with its
 *   events, tests, history, child items, and attachment references.
 * - Validate the `intel`, `riskProfile`, and `riskAssessment` blocks that sit
 *   beside `systems` on the same entity record.
 *
 * Dependencies:
 * - `zod` v3 only. These schemas mirror the canonical embedded contract types
 *   in `@firesquirrel/sync-config` (`embeddedBuildingSystem.ts`); they do not
 *   import them, so this contract source stays free of extra dependencies
 *   (mobile and web packages alias this folder's source).
 *
 * Architecture:
 * - Consumed by `entitySchemas.ts`, which adds the four blocks to the
 *   client-writable projection and (by extension) the complete persisted
 *   document schema. Hosts inherit them; none redefines them.
 * - Every embedded schema is `.passthrough()` deliberately: unknown legacy
 *   extension fields, unresolved lookup codes, and false/zero/null
 *   distinctions must survive a round-trip (G-016).
 *
 * Notes:
 * - Do NOT add defaults to embedded fields: a default rewrites data the client
 *   did not send.
 * - Moved here verbatim from the tenant-api host schema so the embedded blocks
 *   have exactly one definition.
 */

import { z } from 'zod';

const embeddedLookupReferenceSchema = z
  .object({
    lookupId: z.string().optional(),
    lookupType: z.string().optional(),
    code: z.string().optional(),
    name: z.string().optional(),
    rawCode: z.string().optional(),
  })
  .passthrough();

const embeddedSourceProvenanceSchema = z
  .object({
    system: z.string().optional(),
    table: z.string().optional(),
    sourceId: z.union([z.string(), z.number()]).optional(),
    legacyDb: z.string().optional(),
    runId: z.string().optional(),
    migratedAt: z.string().optional(),
  })
  .passthrough();

const embeddedAuditFields = {
  createdAt: z.string().optional(),
  createdBy: z.string().optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
  version: z.number().optional(),
};

const embeddedTombstoneFields = {
  _deleted: z.boolean().optional(),
  deletedAt: z.string().optional(),
  deletedBy: z.string().optional(),
};

const embeddedAttachmentRefSchema = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    attachmentId: z.string().optional(),
    fileName: z.string().optional(),
    blobUrl: z.string().optional(),
    contentType: z.string().optional(),
    sizeBytes: z.number().optional(),
    caption: z.string().optional(),
    description: z.string().optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedSystemEventSchema = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    eventId: z.string().optional(),
    eventType: embeddedLookupReferenceSchema.optional(),
    occurredAt: z.string().optional(),
    receivedAt: z.string().optional(),
    contractor: embeddedLookupReferenceSchema.optional(),
    description: z.string().optional(),
    expiresAt: z.string().optional(),
    passed: z.boolean().nullable().optional(),
    notes: z.string().optional(),
    attachments: z.array(embeddedAttachmentRefSchema).optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedSystemTestSchema = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    testId: z.string().optional(),
    testType: z.string().optional(),
    agency: z.string().optional(),
    inspector: z.string().optional(),
    testedAt: z.string().optional(),
    inspectedAt: z.string().optional(),
    expiresAt: z.string().optional(),
    score: z.union([z.number(), z.string()]).nullable().optional(),
    passed: z.boolean().nullable().optional(),
    notes: z.string().optional(),
    attachments: z.array(embeddedAttachmentRefSchema).optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedSystemChildItemSchema = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    childId: z.string().optional(),
    kind: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    spec: z.record(z.unknown()).optional(),
    attachments: z.array(embeddedAttachmentRefSchema).optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedSystemLinkSchema = z
  .object({
    systemId: z.string().optional(),
    targetId: z.string().optional(),
    relationship: z.string().optional(),
    name: z.string().optional(),
  })
  .passthrough();

const embeddedSystemChildItemsSchema = z
  .object({
    chemicals: z.array(embeddedSystemChildItemSchema).optional(),
    tanks: z.array(embeddedSystemChildItemSchema).optional(),
    keyBoxes: z.array(embeddedSystemChildItemSchema).optional(),
    panels: z.array(embeddedSystemChildItemSchema).optional(),
    detectors: z.array(embeddedSystemChildItemSchema).optional(),
    equipment: z.array(embeddedSystemChildItemSchema).optional(),
    alarms: z.array(embeddedSystemChildItemSchema).optional(),
    pumps: z.array(embeddedSystemChildItemSchema).optional(),
    backupPower: z.array(embeddedSystemLinkSchema).optional(),
    permits: z.array(embeddedSystemLinkSchema).optional(),
  })
  .passthrough();

const embeddedBuildingSystemSchemaObject = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    systemId: z.string().optional(),
    category: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
    status: embeddedLookupReferenceSchema.optional(),
    spec: z.record(z.unknown()).optional(),
    childItems: embeddedSystemChildItemsSchema.optional(),
    events: z.array(embeddedSystemEventSchema).optional(),
    tests: z.array(embeddedSystemTestSchema).optional(),
    history: z.array(embeddedSystemTestSchema).optional(),
    attachments: z.array(embeddedAttachmentRefSchema).optional(),
    sourceTable: z.string().optional(),
    sourceId: z.union([z.string(), z.number()]).optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedRiskFactorSchema = z
  .object({
    factorId: z.string().optional(),
    label: z.string().optional(),
    points: z.number().nullable().optional(),
    selection: embeddedLookupReferenceSchema.optional(),
    evidenceSystemIds: z.array(z.string()).optional(),
    notes: z.string().optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedRiskAssessmentSchemaObject = z
  .object({
    ...embeddedAuditFields,
    factors: z.array(embeddedRiskFactorSchema).optional(),
    policyVersion: z.string().optional(),
    total: z.number().nullable().optional(),
    rank: z.string().optional(),
    color: z.string().optional(),
    manual: z.boolean().optional(),
    complete: z.boolean().optional(),
    reviewedAt: z.string().optional(),
    reviewedBy: z.string().optional(),
    assessedAt: z.string().optional(),
    assessedBy: z.string().optional(),
    notes: z.string().optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedRiskProfileSchemaObject = z
  .object({
    score: z.number().optional(),
    isManual: z.boolean().optional(),
    isComplete: z.boolean().optional(),
    assessedAt: z.string().optional(),
    notes: z.string().optional(),
    rank: z.string().optional(),
    color: z.string().optional(),
  })
  .passthrough();

const intelSnapshotSchemaObject = z
  .object({
    intelId: z.string().optional(),
    category: z.union([z.string(), z.object({ name: z.string().optional() }).passthrough()]).optional(),
    content: z.string().optional(),
    createdAt: z.string().optional(),
  })
  .passthrough();


// ---------------------------------------------------------------------------
// Public exports
//
// The object schemas above carry deeply nested inferred types. Exported as-is
// they push the complete entity document schema past the TypeScript
// declaration-emit limit (TS7056). The runtime schemas are unchanged; only the
// public static type is simplified to the identifying keys plus an open index,
// which matches the passthrough behavior (unknown fields are preserved).
// ---------------------------------------------------------------------------

/** Open record: any key a passthrough embedded block may carry. */
type OpenEmbeddedBlock = { [key: string]: unknown };

/** One embedded building system (alarm, extinguishing, backup power, ...). */
export interface EmbeddedBuildingSystemBlock extends OpenEmbeddedBlock {
  systemId?: string;
  category?: string;
  name?: string;
  description?: string;
  notes?: string;
  spec?: Record<string, unknown>;
  childItems?: Record<string, unknown>;
  events?: Record<string, unknown>[];
  tests?: Record<string, unknown>[];
  history?: Record<string, unknown>[];
  attachments?: Record<string, unknown>[];
}

/** Building intel snapshot entry. */
export interface IntelSnapshotBlock extends OpenEmbeddedBlock {
  intelId?: string;
  content?: string;
  createdAt?: string;
}

/** Risk display summary. */
export interface EmbeddedRiskProfileBlock extends OpenEmbeddedBlock {
  score?: number;
  rank?: string;
  color?: string;
}

/** Ten-factor risk assessment. */
export interface EmbeddedRiskAssessmentBlock extends OpenEmbeddedBlock {
  factors?: Record<string, unknown>[];
  total?: number | null;
  rank?: string;
  color?: string;
}

export const embeddedBuildingSystemSchema =
  embeddedBuildingSystemSchemaObject as unknown as z.ZodType<EmbeddedBuildingSystemBlock>;
export const intelSnapshotSchema =
  intelSnapshotSchemaObject as unknown as z.ZodType<IntelSnapshotBlock>;
export const embeddedRiskProfileSchema =
  embeddedRiskProfileSchemaObject as unknown as z.ZodType<EmbeddedRiskProfileBlock>;
export const embeddedRiskAssessmentSchema =
  embeddedRiskAssessmentSchemaObject as unknown as z.ZodType<EmbeddedRiskAssessmentBlock>;
